import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";

import useDrawerMenu from "hooks/useDrawerMenu";
import useI18n from "hooks/useI18n";
import { DoLogout } from "services/authentication";
import { ReadIdentitySession, IDENTITY_AREA_ROUTES } from "services/identity";
import { getPortalConfig, normalizePortal } from "utils/portal";
import { languages } from "utils/options";
import {
    DrawerBackdrop,
    DrawerBrand,
    DrawerChildButton,
    DrawerChildRow,
    DrawerChildTitle,
    DrawerChildToggle,
    DrawerChildren,
    DrawerChevronIcon,
    DrawerClose,
    DrawerHeader,
    DrawerItemIcon,
    DrawerItemButton,
    DrawerItemMeta,
    DrawerItemTitle,
    DrawerMobileOnlyGroup,
    DrawerPanel,
    DrawerProfileCard,
    DrawerProfileMeta,
    DrawerProfileName,
    DrawerScroll,
    DrawerSection,
    DrawerSubChildButton,
    DrawerSubChildren,
    DrawerUtilityLabel,
} from "./styled";
import { Icon, Load, LoadCenter } from "ui/styled";
import { CoreContext } from "context/CoreContext";

const DRAWER_ICON_FOLDER = "home-drawer";

function normalizePath(value = "") {
    const normalizedValue = `${value}`.split("?")[0].split("#")[0].replace(/\/+$/, "");
    return normalizedValue || "/";
}

export default function DashboardSide({ opened, setOpened, portal }) {
    const history = useHistory();
    const location = useLocation();
    const { setModal, themeMode, user } = useContext(CoreContext);
    const resolvedPortal = normalizePortal(portal);
    const portalConfig = useMemo(() => getPortalConfig(resolvedPortal), [resolvedPortal]);
    const { loading, menu } = useDrawerMenu(resolvedPortal);
    const { language, setLanguage, t } = useI18n();

    const [expandedGroups, setExpandedGroups] = useState({});

    const currentPath = normalizePath(location.pathname);
    const currentPathWithSearch = `${currentPath}${location.search || ''}`;
    const myAccountRoute = portalConfig?.myAccountRoute;
    const certificatesRoute = portalConfig?.certificatesRoute;
    const loginRoute = portalConfig?.loginRoute;
    const identity = ReadIdentitySession();
    const identityAccessRoute = resolvedPortal === 'essentials' ? '/mvu-lite/access'
        : resolvedPortal === 'accelerator' ? '/mvu-accelerator/access' : '/access';
    const iconPrefix = useMemo(
        () => `${DRAWER_ICON_FOLDER}/${themeMode === "light" ? "light" : "dark"}`,
        [themeMode]
    );

    const getDrawerIcon = useCallback((iconName) => (
        iconName ? `${iconPrefix}/${iconName}` : null
    ), [iconPrefix]);
    const getDrawerTitle = useCallback((item) => (
        item?.titleKey ? t(item.titleKey) : item?.title
    ), [t]);

    const chevronIcon = useMemo(() => getDrawerIcon("chevron-down"), [getDrawerIcon]);
    const closeIcon = useMemo(() => getDrawerIcon("close"), [getDrawerIcon]);
    const brandIcon = useMemo(() => getDrawerIcon("MV_Universe_Logo"), [getDrawerIcon]);
    const currentLanguage = useMemo(
        () => languages.find((item) => item.id === language) || languages[0],
        [language]
    );
    const userLabel = useMemo(
        () => `${user?.firstName || ''} ${user?.lastName || ''}`.trim().toUpperCase() || 'USER',
        [user?.firstName, user?.lastName]
    );

    const closeDrawer = useCallback(() => setOpened(false), [setOpened]);

    const handleLogout = useCallback(async () => {
        await DoLogout();
        history.push(loginRoute);
        closeDrawer();
    }, [closeDrawer, history, loginRoute]);

    const runAction = useCallback((action) => {
        if (!action?.type) return;

        if (action.type === 'navigate' && action.value) {
            history.push(action.value);
            closeDrawer();
            return;
        }

        if (action.type === 'modal' && action.value) {
            setModal({ type: action.value });
            closeDrawer();
            return;
        }

        if (action.type === 'download' && action.value) {
            window.location.href = action.value;
            closeDrawer();
        }
    }, [closeDrawer, history, setModal]);

    const isActiveAction = useCallback((action) => {
        if (action?.type !== 'navigate' || !action?.value) return false;

        const normalizedTarget = `${action.value}`.split("#")[0];
        const [targetValue, targetSearch] = normalizedTarget.split("?");
        const targetPath = normalizePath(targetValue);

        if (targetSearch) {
            return currentPathWithSearch === `${targetPath}?${targetSearch}`;
        }

        if (targetPath === '/dashboard') {
            return currentPath === targetPath;
        }

        return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
    }, [currentPath, currentPathWithSearch]);

    const activeKeys = useMemo(() => {
        const keys = {};

        (menu || []).forEach((item) => {
            if (item?.type !== 'group') return;

            const hasActiveChild = (item.children || []).some((child) => {
                if (isActiveAction(child?.action)) return true;
                return (child?.children || []).some((subChild) => isActiveAction(subChild?.action));
            });

            if (hasActiveChild) {
                keys[item.key] = true;
            }
        });

        return keys;
    }, [isActiveAction, menu]);

    useEffect(() => {
        setExpandedGroups((previous) => {
            const next = { ...previous };

            (menu || []).forEach((item) => {
                if (item?.type !== 'group') return;
                if (typeof next[item.key] !== 'boolean') {
                    next[item.key] = !!activeKeys[item.key];
                }
            });

            return next;
        });
    }, [menu, activeKeys]);

    const toggleExpanded = useCallback((key) => {
        setExpandedGroups((previous) => ({
            ...previous,
            [key]: !previous[key],
        }));
    }, []);

    if (!opened) {
        return null;
    }

    return (
        <>
            <DrawerBackdrop onClick={closeDrawer} />

            <DrawerPanel>
                <DrawerHeader>
                    <DrawerBrand>
                        <Icon icon={brandIcon} width={'220px'} />
                    </DrawerBrand>

                    <DrawerClose onClick={closeDrawer}>
                        <Icon icon={closeIcon} width="16px" nomargin />
                    </DrawerClose>
                </DrawerHeader>

                <DrawerScroll>
                    <DrawerSection mobile>
                        <DrawerProfileCard>
                            <DrawerProfileName>{userLabel}</DrawerProfileName>
                            <DrawerProfileMeta>{t('my_account')}</DrawerProfileMeta>
                        </DrawerProfileCard>
                    </DrawerSection>

                    <DrawerSection separator mobile>
                        <DrawerMobileOnlyGroup>
                            <DrawerUtilityLabel>{t('language')}</DrawerUtilityLabel>
                            {languages.map((item) => (
                                <DrawerChildButton
                                    key={item.id}
                                    active={item.id === currentLanguage?.id}
                                    onClick={() => {
                                        setLanguage(item.id);
                                        closeDrawer();
                                    }}
                                >
                                    <DrawerChildTitle active={item.id === currentLanguage?.id}>{item.title}</DrawerChildTitle>
                                </DrawerChildButton>
                            ))}
                        </DrawerMobileOnlyGroup>
                    </DrawerSection>

                    <DrawerSection separator mobile>
                        <DrawerMobileOnlyGroup>
                            <DrawerUtilityLabel>{t('my_account')}</DrawerUtilityLabel>
                            {!identity?.centralUserId ? null : (
                                <DrawerChildButton active={currentPath === identityAccessRoute} onClick={() => runAction({ type: 'navigate', value: identityAccessRoute })}>
                                    <DrawerChildTitle>{t('identity_access_title')}</DrawerChildTitle>
                                </DrawerChildButton>
                            )}
                            {(identity?.areas?.length > 1 ? identity.areas : []).map(area => (
                                <DrawerChildButton key={area} active={false} onClick={() => runAction({ type: 'navigate', value: IDENTITY_AREA_ROUTES[area] })}>
                                    <DrawerChildTitle>{area === 'universe' ? 'MagVenture Universe' : area === 'lite' ? 'MagVenture Universe Lite' : 'MagVenture Accelerator'}</DrawerChildTitle>
                                </DrawerChildButton>
                            ))}
                            {!myAccountRoute ? null : (
                                <DrawerChildButton active={false} onClick={() => runAction({ type: 'navigate', value: myAccountRoute })}>
                                    <DrawerChildTitle>{t('my_account')}</DrawerChildTitle>
                                </DrawerChildButton>
                            )}
                            {!certificatesRoute ? null : (
                                <DrawerChildButton active={false} onClick={() => runAction({ type: 'navigate', value: certificatesRoute })}>
                                    <DrawerChildTitle>{t('my_certificates')}</DrawerChildTitle>
                                </DrawerChildButton>
                            )}
                            <DrawerChildButton active={false} onClick={handleLogout}>
                                <DrawerChildTitle>{t('logout')}</DrawerChildTitle>
                            </DrawerChildButton>
                        </DrawerMobileOnlyGroup>
                    </DrawerSection>

                    {loading ? (
                        <LoadCenter>
                            <Load white={themeMode === 'dark'} />
                        </LoadCenter>
                    ) : (menu || []).map((item) => {
                        const active = item?.type === 'group'
                            ? !!activeKeys[item.key]
                            : isActiveAction(item?.action);

                        const expanded = !!expandedGroups[item.key];

                        return (
                            <DrawerSection key={item.key} separator={item?.separator}>
                                <DrawerItemButton
                                    active={active}
                                    onClick={() => item?.type === 'group' ? toggleExpanded(item.key) : runAction(item?.action)}
                                >
                                    <DrawerItemMeta>
                                        {item?.icon ? (
                                            <DrawerItemIcon
                                                active={active}
                                                icon={getDrawerIcon(active ? item?.activeIcon || item.icon : item.icon)}
                                                nomargin
                                            />
                                        ) : null}
                                        <DrawerItemTitle active={active}>{getDrawerTitle(item)}</DrawerItemTitle>
                                    </DrawerItemMeta>

                                    {item?.type === 'group' ? (
                                        <DrawerChevronIcon
                                            active={active}
                                            expanded={expanded}
                                            icon={chevronIcon}
                                            width="12px"
                                            nomargin
                                        />
                                    ) : null}
                                </DrawerItemButton>

                                {item?.type === 'group' && expanded ? (
                                    <DrawerChildren>
                                        {(item.children || []).map((child) => {
                                            const childActive = isActiveAction(child?.action)
                                                || (child?.children || []).some((subChild) => isActiveAction(subChild?.action));

                                            return (
                                                <div key={`${item.key}-${child.id}`}>
                                                    {child?.children?.length ? (
                                                        <DrawerChildRow>
                                                            <DrawerChildButton
                                                                active={childActive}
                                                                onClick={() => runAction(child?.action)}
                                                            >
                                                                <DrawerChildTitle active={childActive}>{getDrawerTitle(child)}</DrawerChildTitle>
                                                            </DrawerChildButton>

                                                            <DrawerChildToggle
                                                                active={childActive}
                                                                onClick={(event) => {
                                                                    event.preventDefault();
                                                                    event.stopPropagation();
                                                                    toggleExpanded(`${item.key}-${child.id}`);
                                                                }}
                                                            >
                                                                <DrawerChevronIcon
                                                                    active={childActive}
                                                                    expanded={expandedGroups[`${item.key}-${child.id}`]}
                                                                    icon={chevronIcon}
                                                                    width="10px"
                                                                    nomargin
                                                                />
                                                            </DrawerChildToggle>
                                                        </DrawerChildRow>
                                                    ) : (
                                                        <DrawerChildButton
                                                            active={childActive}
                                                            onClick={() => runAction(child?.action)}
                                                        >
                                                            <DrawerChildTitle active={childActive}>{getDrawerTitle(child)}</DrawerChildTitle>
                                                        </DrawerChildButton>
                                                    )}

                                                    {child?.children?.length && expandedGroups[`${item.key}-${child.id}`] ? (
                                                        <DrawerSubChildren>
                                                            {child.children.map((subChild) => (
                                                                <DrawerSubChildButton
                                                                    key={`${item.key}-${child.id}-${subChild.id}`}
                                                                    active={isActiveAction(subChild?.action)}
                                                                    onClick={() => runAction(subChild?.action)}
                                                                >
                                                                    {getDrawerTitle(subChild)}
                                                                </DrawerSubChildButton>
                                                            ))}
                                                        </DrawerSubChildren>
                                                    ) : null}
                                                </div>
                                            );
                                        })}
                                    </DrawerChildren>
                                ) : null}
                            </DrawerSection>
                        );
                    })}
                </DrawerScroll>
            </DrawerPanel>
        </>
    );
}
